import { useEffect, useMemo, useState } from "react";
import "./AdminActivityOccupancyModule.scss";
import { toast } from "react-toastify";
import {
  ActivityOccupancyStatisticsExport,
  OccupancyStatisticsExport,
} from "../../../types/api";
import StatisticsFilterList from "../StatisticsFilterList/StatisticsFilterList";

import { createStatisticsFilterStore } from "../Stores/statisticsFilterStoreFactory";
import { ReportTableSection } from "../Tables/ReportTableSection";
import { MembertypeChart } from "../Charts/MembertypeChart";
import { GuestChart } from "../Charts/GuestChart";

interface AdminActivityOccupancyModuleProps {
  activityOccupancyData: ActivityOccupancyStatisticsExport;
  activityOccupancyFilterStore: ReturnType<typeof createStatisticsFilterStore>;
  startDate: string;
  endDate: string;
  selectedWeekdays: number[];
}

const AdminActivityOccupancyModule = ({
  activityOccupancyData,
  activityOccupancyFilterStore,
  startDate,
  endDate,
  selectedWeekdays,
}: AdminActivityOccupancyModuleProps) => {
  function countWeekdaysFromIntervals(startDate: Date, endDate: Date) {
    const counts = {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
    };

    const start = new Date(
      Date.UTC(
        startDate.getUTCFullYear(),
        startDate.getUTCMonth(),
        startDate.getUTCDate(),
      ),
    );
    const end = new Date(
      Date.UTC(
        endDate.getUTCFullYear(),
        endDate.getUTCMonth(),
        endDate.getUTCDate(),
      ),
    );

    for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
      const dayOfWeek = d.getUTCDay(); // 0 = Sunday
      const isoDay = dayOfWeek;

      if (selectedWeekdays.includes(isoDay)) {
        switch (isoDay) {
          case 1:
            counts.monday++;
            break;
          case 2:
            counts.tuesday++;
            break;
          case 3:
            counts.wednesday++;
            break;
          case 4:
            counts.thursday++;
            break;
          case 5:
            counts.friday++;
            break;
          case 6:
            counts.saturday++;
            break;
          case 0:
            counts.sunday++;
            break;
        }
      }
    }

    return counts;
  }

  const [weekdayCounts, setWeekdayCounts] = useState({
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
  });

  const totalDays = Object.values(weekdayCounts).reduce(
    (acc, count) => acc + count,
    0,
  );
  useEffect(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const counts = countWeekdaysFromIntervals(start, end);
    setWeekdayCounts(counts);
  }, [startDate, endDate, selectedWeekdays]);
  const [chartType, setChartType] = useState<"line" | "pie">("line");

  return (
    <div className="admin-activity-occupancy-module">
      <StatisticsFilterList
        useStore={activityOccupancyFilterStore}
        useGender={true}
        useAgeGroups={true}
      />
      {!activityOccupancyData && (
        <ReportTableSection
          title={"Aktiviteter"}
          columns={[
            { key: "timespan", label: "Tidsperiod", align: "left" },
            {
              key: "monday",
              label: `Måndag (${weekdayCounts.monday})`,
              align: "left",
            },
            {
              key: "tuesday",
              label: `Tisdag (${weekdayCounts.tuesday})`,
              align: "left",
            },
            {
              key: "wednesday",
              label: `Onsdag (${weekdayCounts.wednesday})`,
              align: "left",
            },
            {
              key: "thursday",
              label: `Torsdag (${weekdayCounts.thursday})`,
              align: "left",
            },
            {
              key: "friday",
              label: `Fredag (${weekdayCounts.friday})`,
              align: "left",
            },
            {
              key: "saturday",
              label: `Lördag (${weekdayCounts.saturday})`,
              align: "left",
            },
            {
              key: "sunday",
              label: `Söndag (${weekdayCounts.sunday})`,
              align: "left",
            },
            {
              key: "total",
              label: `Total (${totalDays})`,
              align: "left",
            },
          ]}
          rows={[]}
          useCalcType={false}
          useToggleType={false}
          useActivityType={true}
          onToggleType={(isPie: boolean) =>
            setChartType(isPie ? "pie" : "line")
          }
          useStore={activityOccupancyFilterStore}
          isLoading={false}
        />
      )}
      {activityOccupancyData &&
        activityOccupancyData.activities.map((activity, idx) => (
          <ReportTableSection
            title={activity.activityName}
            key={idx}
            columns={[
              { key: "timespan", label: "Tidsperiod", align: "left" },
              {
                key: "monday",
                label: `Måndag (${weekdayCounts.monday})`,
                align: "left",
              },
              {
                key: "tuesday",
                label: `Tisdag (${weekdayCounts.tuesday})`,
                align: "left",
              },
              {
                key: "wednesday",
                label: `Onsdag (${weekdayCounts.wednesday})`,
                align: "left",
              },
              {
                key: "thursday",
                label: `Torsdag (${weekdayCounts.thursday})`,
                align: "left",
              },
              {
                key: "friday",
                label: `Fredag (${weekdayCounts.friday})`,
                align: "left",
              },
              {
                key: "saturday",
                label: `Lördag (${weekdayCounts.saturday})`,
                align: "left",
              },
              {
                key: "sunday",
                label: `Söndag (${weekdayCounts.sunday})`,
                align: "left",
              },
              {
                key: "total",
                label: `Total (${totalDays})`,
                align: "left",
              },
            ]}
            rows={activity.rows}
            useCalcType={false}
            useToggleType={false}
            useActivityType={true}
            onToggleType={(isPie: boolean) =>
              setChartType(isPie ? "pie" : "line")
            }
            useStore={activityOccupancyFilterStore}
            isLoading={false}
          />
        ))}
      <div className="occupancy-spacer"></div>
    </div>
  );
};
export default AdminActivityOccupancyModule;
